import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { sendContactMessage } from '../services/contactServices';
import { useLocale } from '../context/LocaleContext';
import SeoHead from '../components/SeoHead';
import { useT } from '../hooks/useT';
import styled from 'styled-components';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 0.5rem 0;
`;

const Text = styled.p`
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 2rem 0;
  line-height: 1.6;
`;

const Form = styled.form`
  max-width: 42rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  display: block;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  height: 3rem;
  padding: 0 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  outline: none;
  border: 2px solid transparent;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  outline: none;
  border: 2px solid transparent;
  resize: vertical;
  min-height: 120px;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const SubmitBtn = styled.button`
  align-self: flex-start;
  height: 3rem;
  padding: 0 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &:hover {
    opacity: 0.9;
  }
`;

const SuccessCard = styled(motion.div)`
  max-width: 42rem;
  margin-top: 1rem;
  padding: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(34, 197, 94, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  color: hsl(142, 71%, 45%);
`;

const SuccessTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 1rem 0 0 0;
`;

const SuccessDesc = styled.p`
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.5rem 0 0 0;
`;

function Contact() {
  const { prependPath } = useLocale();
  const t = useT();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await sendContactMessage(form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <SeoHead title="Contact | CiteMatch" description="Contact the CiteMatch team." noindex />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <BackLink to={prependPath('/')}><ArrowLeft size={16} strokeWidth={2.5} /> {t('common.back')}</BackLink>
        <Title>Contact</Title>
        <Text>Get in touch with the CiteMatch team. We'd love to hear from you.</Text>

        {submitted ? (
          <SuccessCard initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <SuccessIcon>
              <CheckCircle size={32} />
            </SuccessIcon>
            <SuccessTitle>Message sent</SuccessTitle>
            <SuccessDesc>We'll get back to you as soon as we can.</SuccessDesc>
          </SuccessCard>
        ) : (
          <Form onSubmit={handleSubmit}>
            {error && <p style={{ color: 'hsl(0, 84%, 60%)', margin: '0 0 0.25rem 0' }}>{error}</p>}
            <div>
              <Label>Name *</Label>
              <Input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label>Subject *</Label>
              <Input
                type="text"
                required
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="What's this about?"
              />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea
                required
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Your message"
              />
            </div>
            <SubmitBtn type="submit" disabled={loading}>
              <Send size={16} strokeWidth={2.5} /> Send message
            </SubmitBtn>
          </Form>
        )}
      </Container>
      <Footer />
    </PageLayout>
  );
}

export default Contact;
